import React, { useState, useEffect } from 'react';
import { FiFilter, FiSearch, FiEye, FiEdit2, FiPackage, FiTruck, FiCheck, FiClock, FiRefreshCw } from 'react-icons/fi';
import authService from '../../utils/authService';

interface Order {
  id: number;
  order_number: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  total_amount: number;
  total_amount_formatted: string;
  currency: string;
  order_status: string;
  fulfillment_status: string;
  payment_status: string;
  items?: Array<{
    description: string;
    quantity: number;
    amount: number;
  }>;
  shipping_address?: any;
  billing_address?: any;
  created_at: string;
  updated_at: string;
  notes?: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to format product information
  const formatProductInfo = (items?: Array<{description: string; quantity: number; amount: number}>) => {
    if (!items || items.length === 0) {
      return { name: 'N/A', details: '' };
    }

    if (items.length === 1) {
      const item = items[0];
      // Extract product name and size from description
      // Format: "Nike - Air Jordan 1 - Off-White - UNC" or similar
      const parts = item.description.split(' - ');
      const productName = parts.length > 1 ? `${parts[0]} - ${parts[1]}` : item.description;
      const size = parts.find(part => 
        part.toLowerCase().includes('size') || 
        /^\d+(\.\d+)?$/.test(part) || 
        /^(XS|S|M|L|XL|XXL)$/i.test(part) ||
        /^\d+(\.\d+)?\s*(US|UK|EU)?$/i.test(part)
      );
      
      return {
        name: productName,
        details: `Qty: ${item.quantity}${size ? ` • Size: ${size}` : ''}`
      };
    } else {
      return {
        name: `${items.length} items`,
        details: `Total qty: ${items.reduce((sum, item) => sum + item.quantity, 0)}`
      };
    }
  };
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [fulfillmentFilter, setFulfillmentFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal state
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch orders
  const fetchOrders = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });
      
      if (statusFilter) params.append('status', statusFilter);
      if (fulfillmentFilter) params.append('fulfillment_status', fulfillmentFilter);
      
      // Use authService for consistent authentication
      const data = await authService.apiRequest(`/api/admin/orders?${params}`, 'GET');
      
      setOrders(data.orders || []);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId: number, orderStatus?: string, fulfillmentStatus?: string, notes?: string) => {
    try {
      setIsUpdating(true);
      
      // Use authService for consistent authentication
      const data = await authService.apiRequest('/api/admin/orders', 'PUT', {
        orderId,
        orderStatus,
        fulfillmentStatus,
        notes
      });
      
      // Update the order in the local state
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, ...data.order } : order
      ));
      
      // Update selected order if it's the one being updated
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, ...data.order });
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update order');
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, fulfillmentFilter]);

  const getStatusColor = (status: string, type: 'order' | 'fulfillment') => {
    if (type === 'fulfillment') {
      switch (status) {
        case 'fulfilled': return 'bg-green-500/10 text-green-400';
        case 'unfulfilled': return 'bg-amber-500/10 text-amber-400';
        default: return 'bg-gray-500/10 text-gray-400';
      }
    } else {
      switch (status) {
        case 'completed': return 'bg-green-500/10 text-green-400';
        case 'cancelled': return 'bg-red-500/10 text-red-400';
        case 'new': return 'bg-amber-500/10 text-amber-400';
        default: return 'bg-gray-500/10 text-gray-400';
      }
    }
  };

  const getStatusText = (status: string, type: 'order' | 'fulfillment') => {
    if (type === 'fulfillment') {
      switch (status) {
        case 'fulfilled': return 'Shipped';
        case 'unfulfilled': return 'Not Shipped';
        default: return status;
      }
    } else {
      switch (status) {
        case 'new': return 'New Order';
        case 'completed': return 'Completed';
        case 'cancelled': return 'Cancelled';
        default: return status;
      }
    }
  };

  // Order Detail Modal
  const OrderDetailModal: React.FC = () => {
    if (!selectedOrder) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-background-light rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-800">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-primary">Order Details</h2>
              <button
                onClick={() => setIsOrderModalOpen(false)}
                className="text-gray-400 hover:text-primary"
              >
                ✕
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Order Info */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-accent mb-3 uppercase tracking-wider">Order Information</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="text-gray-400">Order #:</span> <span className="font-mono">{selectedOrder.order_number}</span></div>
                  <div><span className="text-gray-400">Date:</span> {new Date(selectedOrder.created_at).toLocaleString()}</div>
                  <div><span className="text-gray-400">Total:</span> {selectedOrder.total_amount_formatted}</div>
                  <div><span className="text-gray-400">Payment:</span> <span className="capitalize">{selectedOrder.payment_status}</span></div>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-accent mb-3 uppercase tracking-wider">Customer Information</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="text-gray-400">Name:</span> {selectedOrder.customer_name || 'N/A'}</div>
                  <div><span className="text-gray-400">Email:</span> {selectedOrder.customer_email || 'N/A'}</div>
                  <div><span className="text-gray-400">Phone:</span> {selectedOrder.customer_phone || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Status Management */}
            <div className="border border-gray-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-accent mb-4 uppercase tracking-wider">Order Status</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Order Status</label>
                  <select
                    value={selectedOrder.order_status}
                    onChange={(e) => updateOrderStatus(selectedOrder.id, e.target.value, undefined)}
                    disabled={isUpdating}
                    className="w-full p-2 bg-background border border-gray-700 rounded text-primary"
                  >
                    <option value="new">New Order</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Fulfillment Status</label>
                  <select
                    value={selectedOrder.fulfillment_status}
                    onChange={(e) => updateOrderStatus(selectedOrder.id, undefined, e.target.value)}
                    disabled={isUpdating}
                    className="w-full p-2 bg-background border border-gray-700 rounded text-primary"
                  >
                    <option value="unfulfilled">Not Shipped</option>
                    <option value="fulfilled">Shipped</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Items */}
            {selectedOrder.items && selectedOrder.items.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-accent mb-3 uppercase tracking-wider">Order Items</h3>
                <div className="space-y-2">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-background border border-gray-800 rounded">
                      <div>
                        <p className="text-primary font-medium">{item.description}</p>
                        <p className="text-sm text-gray-400">Quantity: {item.quantity}</p>
                      </div>
                      <p className="text-primary font-medium">${(item.amount / 100).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Addresses */}
            {selectedOrder.shipping_address && (
              <div>
                <h3 className="text-sm font-medium text-accent mb-3 uppercase tracking-wider">Shipping Address</h3>
                <div className="p-3 bg-background border border-gray-800 rounded text-sm">
                  <p>{selectedOrder.shipping_address.line1}</p>
                  {selectedOrder.shipping_address.line2 && <p>{selectedOrder.shipping_address.line2}</p>}
                  <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.postal_code}</p>
                  <p>{selectedOrder.shipping_address.country}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-primary">Orders Management</h1>
          <p className="text-gray-400">Manage and track all customer orders</p>
        </div>
        <button
          onClick={() => fetchOrders(pagination.page)}
          disabled={loading}
          className="flex items-center px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50"
        >
          <FiRefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-background-light rounded-lg p-4 border border-gray-800">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-64">
            <label className="block text-sm text-gray-400 mb-2">Search Orders</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by order number, customer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-background border border-gray-700 rounded text-primary placeholder-gray-400"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-2">Order Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-background border border-gray-700 rounded text-primary"
            >
              <option value="">All Orders</option>
              <option value="new">New Orders</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-2">Fulfillment</label>
            <select
              value={fulfillmentFilter}
              onChange={(e) => setFulfillmentFilter(e.target.value)}
              className="px-3 py-2 bg-background border border-gray-700 rounded text-primary"
            >
              <option value="">All Fulfillment</option>
              <option value="unfulfilled">Not Shipped</option>
              <option value="fulfilled">Shipped</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-background-light rounded-lg border border-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background border-b border-gray-800">
              <tr className="text-left">
                <th className="px-4 py-3 text-sm font-medium text-gray-400">Order</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-400">Product(s)</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-400">Customer</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-400">Date</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-400">Status</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-400">Fulfillment</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-400">Total</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded animate-pulse"></div></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded animate-pulse"></div></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded animate-pulse"></div></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded animate-pulse"></div></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded animate-pulse"></div></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded animate-pulse"></div></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded animate-pulse"></div></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded animate-pulse"></div></td>
                  </tr>
                ))
              ) : orders.length > 0 ? (
                orders.map((order) => {
                  const productInfo = formatProductInfo(order.items);
                  return (
                    <tr key={order.id} className="hover:bg-background/50">
                      <td className="px-4 py-3">
                        <div className="font-mono text-sm">{order.order_number}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <div className="text-primary font-medium">{productInfo.name}</div>
                          <div className="text-gray-400 text-xs">{productInfo.details}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <div className="text-primary">{order.customer_name || 'N/A'}</div>
                          <div className="text-gray-400 text-xs">{order.customer_email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.order_status, 'order')}`}>
                          {getStatusText(order.order_status, 'order')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.fulfillment_status, 'fulfillment')}`}>
                          {getStatusText(order.fulfillment_status, 'fulfillment')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">{order.total_amount_formatted}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setIsOrderModalOpen(true);
                          }}
                          className="text-accent hover:text-accent/80 p-1"
                          title="View Details"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                    No orders found. Orders will appear here once customers make purchases.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="p-4 border-t border-gray-800 flex justify-between items-center">
            <div className="text-sm text-gray-400">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} orders
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchOrders(pagination.page - 1)}
                disabled={pagination.page <= 1 || loading}
                className="px-3 py-1 bg-background border border-gray-700 rounded text-sm hover:bg-background-light disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => fetchOrders(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages || loading}
                className="px-3 py-1 bg-background border border-gray-700 rounded text-sm hover:bg-background-light disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {isOrderModalOpen && <OrderDetailModal />}
    </div>
  );
};

export default Orders; 